// /cliente/cargar-tarjeta/:clienteId
// Plan C (7-jul-2026): reemplaza el flow de preapproval (redirect a MP)
// por form propio con SDK MP JS. Ventajas:
//  - Control total del monto/fecha por cobro (bonificación 3 meses OK)
//  - Sin doble cobro (MP no ejecuta auto_recurring, cron D-1 hace todo)
//  - UX embebido en limpack.uy (no redirect a MP)
// Seguridad: MP JS SDK tokeniza en el navegador. Nunca vemos la tarjeta real.
// Solo guardamos customer_id + card_id + brand + last4 (SAQ A compliance).

import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'

const MP_SDK_URL = 'https://sdk.mercadopago.com/js/v2'

export default function CargarTarjeta() {
  const { profile, user } = useAuth()
  const { clienteId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [cliente, setCliente] = useState(null)
  const [kit, setKit] = useState(null)
  const [proximaEntrega, setProximaEntrega] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [autorizando, setAutorizando] = useState(false)
  const [msg, setMsg] = useState(null)
  const [publicKey, setPublicKey] = useState('')
  const [ambiente, setAmbiente] = useState('prod')
  const [mpReady, setMpReady] = useState(false)
  const mpRef = useRef(null)

  // Form fields
  const [cardNumber, setCardNumber] = useState('')
  const [cardholderName, setCardholderName] = useState('')
  const [expirationDate, setExpirationDate] = useState('') // MM/YY
  const [securityCode, setSecurityCode] = useState('')
  const [ci, setCi] = useState('')

  useEffect(() => { if (clienteId) cargar() }, [clienteId])

  // Al volver de MP con ?tarjeta=ok o ?preapproval=ok (legacy) mostramos éxito
  useEffect(() => {
    if (searchParams.get('tarjeta') === 'ok' || searchParams.get('preapproval') === 'ok') {
      setMsg({ tipo: 'ok', txt: '¡Tarjeta guardada! Los cobros mensuales van a hacerse automáticos.' })
    }
  }, [searchParams])

  // Cargar SDK MP JS + configuración
  useEffect(() => {
    async function initMp() {
      // 1. Traer public key desde app_config
      const { data: cfg } = await supabase.from('app_config').select('key, value')
        .in('key', ['MP_AMBIENTE', 'MP_PUBLIC_KEY_PROD', 'MP_PUBLIC_KEY_TEST'])
      const cfgMap = {}
      ;(cfg || []).forEach(c => {
        cfgMap[c.key] = typeof c.value === 'string' ? c.value.replace(/^"|"$/g, '') : c.value
      })
      const amb = (cfgMap.MP_AMBIENTE || 'prod').toLowerCase()
      const pk = amb === 'prod' ? cfgMap.MP_PUBLIC_KEY_PROD : cfgMap.MP_PUBLIC_KEY_TEST
      setAmbiente(amb)
      setPublicKey(pk)

      // 2. Cargar SDK JS si no está cargado
      if (!window.MercadoPago) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script')
          s.src = MP_SDK_URL
          s.onload = resolve
          s.onerror = () => reject(new Error('No se pudo cargar SDK MP'))
          document.body.appendChild(s)
        }).catch(e => {
          setMsg({ tipo: 'err', txt: 'Error cargando MercadoPago: ' + e.message })
          return
        })
      }

      // 3. Instanciar MercadoPago
      if (window.MercadoPago && pk) {
        mpRef.current = new window.MercadoPago(pk, { locale: 'es-UY' })
        setMpReady(true)
      }
    }
    initMp()
  }, [])

  async function cargar() {
    setCargando(true)
    try {
      const [cliRes, entRes] = await Promise.all([
        supabase.from('clientes')
          .select('id, alias, titular_id, kit_actual, mp_customer_id, mp_card_id, mp_card_last4, mp_card_brand, mp_preapproval_status, kit:kits!clientes_kit_actual_fkey(nombre, precio_susc)')
          .eq('id', clienteId).single(),
        supabase.from('entregas')
          .select('fecha_programada')
          .eq('cliente_id', clienteId)
          .in('estado', ['pendiente_pago', 'programada'])
          .gte('fecha_programada', new Date().toISOString().slice(0, 10))
          .order('fecha_programada', { ascending: true })
          .limit(1),
      ])
      if (cliRes.error) throw cliRes.error
      setCliente(cliRes.data)
      setKit(cliRes.data?.kit)
      setProximaEntrega(entRes.data?.[0]?.fecha_programada || null)
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error cargando: ' + e.message })
    }
    setCargando(false)
  }

  async function guardarTarjeta(e) {
    e?.preventDefault()

    if (!mpReady || !mpRef.current) {
      setMsg({ tipo: 'err', txt: 'MercadoPago aún no está listo. Esperá 2 segundos y probá de nuevo.' })
      return
    }

    const numero = cardNumber.replace(/\s/g, '')
    const [mes, anio] = expirationDate.split('/').map(s => (s || '').trim())
    const anioFull = anio && anio.length === 2 ? '20' + anio : anio

    // Validación básica
    if (!numero || numero.length < 13) return setMsg({ tipo: 'err', txt: 'Número de tarjeta inválido.' })
    if (!cardholderName || cardholderName.trim().length < 2) return setMsg({ tipo: 'err', txt: 'Nombre del titular requerido.' })
    if (!mes || !anioFull || Number(mes) < 1 || Number(mes) > 12) return setMsg({ tipo: 'err', txt: 'Vencimiento inválido (MM/YY).' })
    if (!securityCode || securityCode.length < 3) return setMsg({ tipo: 'err', txt: 'Código de seguridad inválido.' })
    if (!ci || ci.replace(/\D/g, '').length < 7) return setMsg({ tipo: 'err', txt: 'Cédula requerida.' })

    setAutorizando(true)
    setMsg({ tipo: 'info', txt: 'Validando tu tarjeta con MercadoPago… puede tardar unos segundos.' })

    try {
      // 1. Tokenizar la tarjeta con MP JS SDK (los datos NO pasan por Limpack)
      const tokenResp = await mpRef.current.createCardToken({
        cardNumber: numero,
        cardholderName: cardholderName.trim(),
        cardExpirationMonth: mes.padStart(2, '0'),
        cardExpirationYear: anioFull,
        securityCode,
        identificationType: 'CI',
        identificationNumber: ci.replace(/\D/g, ''),
      })
      if (!tokenResp?.id) {
        throw new Error('MercadoPago no devolvió token (' + JSON.stringify(tokenResp).slice(0, 100) + ')')
      }

      // 2. Enviar token al backend Limpack
      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mp-guardar-tarjeta`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cliente_id: cliente.id,
          card_token: tokenResp.id,
        }),
      })
      const data = await resp.json()
      if (!resp.ok || !data.ok) {
        const detalle = data.detalle
          ? ' · ' + (data.detalle.message || data.detalle.cause?.[0]?.description || JSON.stringify(data.detalle).slice(0, 200))
          : ''
        const paso = data.paso ? ` [paso: ${data.paso}]` : ''
        setMsg({ tipo: 'err', txt: 'Error: ' + (data.error || 'no se pudo guardar') + detalle + paso })
        console.error('mp-guardar-tarjeta full response:', data)
        setAutorizando(false)
        return
      }

      // 3. Éxito → recargar datos y mostrar mensaje
      setMsg({ tipo: 'ok', txt: `✅ ¡Tarjeta guardada! ${data.brand?.toUpperCase() || 'Tarjeta'} ****${data.last4} · Redirigiendo a tu panel en 5 seg…` })
      setCardNumber(''); setCardholderName(''); setExpirationDate(''); setSecurityCode(''); setCi('')
      await cargar()
      setAutorizando(false)
      // Redirect al panel después de 5 seg (antes 2 — muy rápido, no se veía el mensaje)
      setTimeout(() => navigate('/cliente?tarjeta=ok'), 5000)
    } catch (e) {
      const msgErr = e?.message || String(e)
      setMsg({ tipo: 'err', txt: 'Error tokenizando: ' + msgErr })
      console.error('createCardToken error:', e)
      setAutorizando(false)
    }
  }

  if (cargando) return (
    <ClienteLayout title="Pagar tu pedido">
      <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Cargando…</div>
    </ClienteLayout>
  )

  const yaAutorizada = cliente?.mp_customer_id && cliente?.mp_card_id

  return (
    <ClienteLayout title="Método de pago">
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 14px 60px' }}>
        <header style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, letterSpacing: '.05em', color: '#94A3B8', textTransform: 'uppercase', fontWeight: 700 }}>
            MI CUENTA · MÉTODO DE PAGO
          </div>
          <h1 style={{ fontSize: 22, color: '#fff', margin: '4px 0 0' }}>
            {yaAutorizada ? 'Tu tarjeta guardada' : 'Guardá tu tarjeta'}
          </h1>
          {kit && (
            <p style={{ fontSize: 13, color: '#94A3B8', marginTop: 4 }}>
              Para el cobro mensual de tu suscripción <strong style={{ color: '#00A8B5' }}>{kit.nombre}</strong> · $ {Number(kit.precio_susc).toLocaleString('es-UY')}/mes
            </p>
          )}
          {ambiente !== 'prod' && (
            <div style={{ display: 'inline-block', marginTop: 8, padding: '2px 8px', background: '#FEF3C7', color: '#78350F', fontSize: 11, borderRadius: 4, fontWeight: 700 }}>
              ⚠ Modo TEST — usá tarjetas de prueba
            </div>
          )}
        </header>

        {msg && (
          <div onClick={() => setMsg(null)} style={{
            padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13, cursor: 'pointer',
            background: msg.tipo === 'ok' ? '#DCFCE7' : msg.tipo === 'err' ? '#FEE2E2' : '#DBEAFE',
            color: msg.tipo === 'ok' ? '#166534' : msg.tipo === 'err' ? '#991B1B' : '#1E40AF',
          }}>
            {msg.txt} <span style={{ float: 'right', opacity: .5 }}>×</span>
          </div>
        )}

        {!cliente ? (
          <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', padding: 14, borderRadius: 8, color: '#78350F' }}>
            No encontramos tu suscripción. <Link to="/cliente/cuenta" style={{ color: '#1A3461', fontWeight: 700 }}>Volver a Mi Cuenta</Link>
          </div>
        ) : yaAutorizada ? (
          <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#DCFCE7', color: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 900 }}>✓</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#1A3461' }}>Tarjeta guardada</div>
                <div style={{ fontSize: 12, color: '#5F5E5A' }}>Cobramos el día anterior a cada entrega</div>
              </div>
            </div>
            <div style={{ padding: 12, background: '#F8FAFC', borderRadius: 8, fontSize: 12.5, color: '#334155', lineHeight: 1.55 }}>
              Tarjeta registrada: <strong>{cliente.mp_card_brand?.toUpperCase() || 'Tarjeta'} ****{cliente.mp_card_last4}</strong>.
              <br/>Cobramos automático el día anterior a cada entrega. Podés cancelar la suscripción cuando quieras.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button
                onClick={() => window.location.reload()}
                disabled={autorizando}
                style={{ padding: '9px 16px', background: '#fff', color: '#1A3461', border: '1px solid #E2E8F0', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                Cambiar tarjeta
              </button>
              <Link to="/cliente" style={{ padding: '9px 16px', background: '#1A3461', color: '#fff', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>Volver al panel</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={guardarTarjeta} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 22 }}>
            {/* Logo MP como elemento principal — es el sello de confianza del pago.
                Logo bien grande a la izquierda (52px alto = 128px ancho), copy a la derecha. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid #F1F5F9' }}>
              <img
                src="/mp-logo.svg"
                alt="Mercado Pago"
                style={{ display: 'block', height: 52, width: 'auto', flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1A3461', marginBottom: 4 }}>Datos de tu tarjeta</div>
                <p style={{ fontSize: 12, color: '#5F5E5A', margin: 0, lineHeight: 1.45 }}>
                  Tus datos se guardan directamente con <strong>MercadoPago</strong> (PCI DSS Level 1).
                  Limpack nunca ve tu tarjeta ni el CVV.
                </p>
              </div>
            </div>
            <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 16px 0', lineHeight: 1.5 }}>
              MP cobra <strong>automático cada 30 días</strong> desde la primera autorización.
            </p>

            <div style={{ marginBottom: 16, padding: 12, background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, fontSize: 12, color: '#78350F', lineHeight: 1.5 }}>
              ⚡ <strong>Validación de tarjeta:</strong> MercadoPago hace un cobro de <strong>$20 UYU</strong> y te lo reembolsa en pocas horas.
              Vas a ver el cobro y su reembolso en tu resumen — el neto es <strong>$0</strong>. Es la forma que usa MP para verificar que tu tarjeta funciona.
              Si algo falla, te avisamos en el momento.
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <label>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>Número de tarjeta</div>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  value={cardNumber}
                  onChange={e => setCardNumber(e.target.value.replace(/[^\d\s]/g, '').slice(0, 19))}
                  placeholder="1234 5678 9012 3456"
                  style={inp}
                />
              </label>

              <label>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>Nombre y apellido</div>
                <input
                  type="text"
                  autoComplete="cc-name"
                  value={cardholderName}
                  onChange={e => setCardholderName(e.target.value)}
                  placeholder="Tal como figura en la tarjeta"
                  style={inp}
                />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>Vencimiento</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    value={expirationDate}
                    onChange={e => {
                      let v = e.target.value.replace(/\D/g, '').slice(0, 4)
                      if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2)
                      setExpirationDate(v)
                    }}
                    placeholder="MM/YY"
                    style={inp}
                  />
                </label>
                <label>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>CVV</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    value={securityCode}
                    onChange={e => setSecurityCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="123"
                    style={inp}
                  />
                </label>
              </div>

              <label>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>Cédula del titular</div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={ci}
                  onChange={e => setCi(e.target.value)}
                  placeholder="Ej: 36955508"
                  style={inp}
                />
                <div style={{ fontSize: 11, color: '#64748B', marginTop: 3 }}>7 u 8 dígitos, con o sin puntos y guión</div>
              </label>
            </div>

            <button
              type="submit"
              disabled={autorizando || !mpReady}
              style={{
                marginTop: 22, width: '100%', padding: '13px 22px',
                background: autorizando || !mpReady ? '#94A3B8' : '#009EE3',
                color: '#fff', border: 'none', borderRadius: 10,
                fontWeight: 800, fontSize: 15,
                cursor: autorizando || !mpReady ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
            >
              {!mpReady ? 'Cargando MercadoPago…' : autorizando ? 'Validando con MercadoPago…' : (<>🔒 Validar y guardar tarjeta</>)}
            </button>

            <div style={{ marginTop: 18, padding: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
              🔒 <strong>Pago 100% seguro</strong> · Los datos van cifrados directo a MercadoPago (Certificación PCI DSS Level 1).
              Nunca guardamos tu número de tarjeta ni CVV en nuestros servidores.
            </div>
          </form>
        )}
      </div>
    </ClienteLayout>
  )
}

const inp = {
  width: '100%',
  padding: '11px 14px',
  border: '1.5px solid #E2E8F0',
  borderRadius: 8,
  fontSize: 15,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  background: '#fff',
  color: '#0F172A',
}
